import React, { useEffect, useState } from 'react';
import { Spin, Table, Input, Button } from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUndisbursedSalesAction,
  updateUndisbursedSaleAction,
  updateSalePrincipalAction,
  updateUnapprovedSaleAction,
} from '../../Sales/store/salesActions';

interface Sale {
  _id: string;
  saleNumber: string;
  customer: any;
  principal: number;
  onlinePrincipal?: number;
  offlinePrincipal?: number;
  documentCharge: number;
  [key: string]: any;
}

export default function CashDisbursementsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const { undisbursedSales }: any = useSelector<any>((state) => state?.sales);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [onlineValue, setOnlineValue] = useState<string>('');
  const [offlineValue, setOfflineValue] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  const getSales = async (): Promise<void> => {
    await getUndisbursedSalesAction(dispatch, {});
    setProcessing(false);
    setEditingId(null);
    setOnlineValue('');
    setOfflineValue('');
  };

  useEffect(() => {
    void getSales();
  }, []);

  const handleEdit = (record: Sale) => {
    setEditingId(record._id);
    setOnlineValue(String(record.onlinePrincipal ?? ''));
    setOfflineValue(String(record.offlinePrincipal ?? ''));
  };

  const handleSave = async (sale: Sale) => {
    const online = Number(onlineValue);
    const offline = Number(offlineValue);

    if (online + offline !== sale.principal) {
      alert('Online Principal + Offline Principal must match Principal amount');
      setProcessing(false);
      return;
    }

    setProcessing(true);
    await updateSalePrincipalAction(dispatch, {
      id: sale._id,
      onlinePrincipal: online,
      offlinePrincipal: offline,
    });
    await getSales();
  };

  const handleUpdate = async (sale: Sale) => {
    setProcessing(true);
    await updateUndisbursedSaleAction(dispatch, { id: sale._id });
    await getSales();
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Sale, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Sale Number',
      dataIndex: 'saleNumber',
      key: 'saleNumber',
      render: (_: any, record: Sale) => <p>{record.saleNumber}</p>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (_: any, record: Sale) => (
        <img src={record.customer?.photo} style={{ height: 130, width: 130 }} />
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: any, record: Sale) => <p>{record.principal}</p>,
    },
    {
      title: 'Online Principal',
      dataIndex: 'onlinePrincipal',
      key: 'onlinePrincipal',
      render: (_: any, record: Sale) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineValue}
            onChange={(e) => {
              setOnlineValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlinePrincipal}</p>
        ),
    },
    {
      title: 'Offline Principal',
      dataIndex: 'offlinePrincipal',
      key: 'offlinePrincipal',
      render: (_: any, record: Sale) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineValue}
            onChange={(e) => {
              setOfflineValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlinePrincipal}</p>
        ),
    },
    {
      title: 'Edit',
      dataIndex: 'edit',
      key: 'edit',
      width: 120,
      render: (_: any, record: Sale) =>
        editingId === record._id ? (
          <Button
            icon={<SaveOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            loading={processing}
            onClick={() => {
              void handleSave(record);
            }}
            disabled={
              Number(onlineValue) + Number(offlineValue) !== record.principal
            }
          ></Button>
        ) : (
          <Button
            icon={<EditOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            onClick={() => {
              handleEdit(record);
            }}
          ></Button>
        ),
    },
    {
      title: 'Customer charges',
      dataIndex: 'documentCharge',
      key: 'documentCharge',
      render: (_: any, record: Sale) => <p>{record.documentCharge}</p>,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 300,
      render: (_: any, record: Sale) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="search__button"
            style={{
              background: !processing ? '#27ae60' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !processing ? 'pointer' : 'not-allowed',
              opacity: !processing ? 1 : 0.7,
            }}
            disabled={processing}
            onClick={() => {
              if (!processing) {
                setProcessing(true);
                void handleUpdate(record);
              }
            }}
          >
            Cash Disbursed
          </button>
          <button
            className="search__button"
            style={{
              background: !processing ? '#e74c3c' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !processing ? 'pointer' : 'not-allowed',
              opacity: !processing ? 1 : 0.7,
            }}
            disabled={processing}
            onClick={async () => {
              if (!processing) {
                setProcessing(true);
                // call approval endpoint with reject
                await updateUnapprovedSaleAction(dispatch, { id: record._id, status: 'reject' });
                await getSales();
              }
            }}
          >
            Reject
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(undisbursedSales.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table
          dataSource={undisbursedSales}
          columns={columns}
          rowKey="_id"
          pagination={false}
        />
      </div>
    </div>
  );
}
